import { Close, Settings } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import {
  FEATURE_STATES,
  FEATURES,
  FeatureState,
} from "../../../constants/features";
import { hostname } from "../../../utils";
import TierFeaturesDialog from "./TierFeaturesDialog";

// Default features for preset tiers (matching backend FEATURE_ACCESS_MAPPING)
const getDefaultFeaturesForTier = (
  tierName: string
): { [key: string]: FeatureState } => {
  const features: { [key: string]: FeatureState } = {};

  // Default all features to enabled for paid tiers, locked for free
  const isFree = tierName.toLowerCase() === "free";
  const isEntrepreneur = tierName.toLowerCase() === "entrepreneur";
  const isGrowth = tierName.toLowerCase() === "growth";
  const isBusiness = tierName.toLowerCase() === "business";

  // Core features - enabled for all
  features[FEATURES.DASHBOARD] = FEATURE_STATES.ENABLED;
  features[FEATURES.CHAT] = FEATURE_STATES.ENABLED;
  features[FEATURES.EMPLOYEE] = FEATURE_STATES.ENABLED;
  features[FEATURES.KNOWLEDGE_BASE] = FEATURE_STATES.ENABLED;

  // Most features - locked for free, enabled for paid
  const standardFeatures = [
    FEATURES.WORKFLOW,
    FEATURES.SMART_LIST,
    FEATURES.SEQUENCE,
    FEATURES.INBOX,
    FEATURES.CONTENT_ENGINE,
    FEATURES.AGENT,
    FEATURES.COLLABORATORS,
    FEATURES.INTEGRATIONS,
    FEATURES.IMAGE,
    FEATURES.AUDIO,
  ];

  standardFeatures.forEach((feature) => {
    features[feature] = isFree ? FEATURE_STATES.LOCKED : FEATURE_STATES.ENABLED;
  });

  // Video - only for Business and above
  features[FEATURES.VIDEO] = isBusiness
    ? FEATURE_STATES.ENABLED
    : FEATURE_STATES.LOCKED;

  return features;
};

// Chat-only features (dashboard, employee, chat only - everything else hidden)
// includeCollaborators: true for presets with multiple seats (like chat-b)
const getChatOnlyFeatures = (includeCollaborators: boolean = false): { [key: string]: FeatureState } => {
  const features: { [key: string]: FeatureState } = {};

  // Only these three features are enabled
  features[FEATURES.DASHBOARD] = FEATURE_STATES.ENABLED;
  features[FEATURES.CHAT] = FEATURE_STATES.ENABLED;
  features[FEATURES.EMPLOYEE] = FEATURE_STATES.ENABLED;

  // Enable collaborators for multi-seat chat presets
  if (includeCollaborators) {
    features[FEATURES.COLLABORATORS] = FEATURE_STATES.ENABLED;
  } else {
    features[FEATURES.COLLABORATORS] = FEATURE_STATES.HIDDEN;
  }

  // All other features are hidden
  const hiddenFeatures = [
    FEATURES.KNOWLEDGE_BASE,
    FEATURES.WORKFLOW,
    FEATURES.SMART_LIST,
    FEATURES.SEQUENCE,
    FEATURES.INBOX,
    FEATURES.CONTENT_ENGINE,
    FEATURES.AGENT,
    FEATURES.INTEGRATIONS,
    FEATURES.IMAGE,
    FEATURES.AUDIO,
    FEATURES.VIDEO,
  ];

  hiddenFeatures.forEach((feature) => {
    features[feature] = FEATURE_STATES.HIDDEN;
  });

  return features;
};

// Preset tier configurations
const PRESET_TIERS = [
  {
    name: "Entrepreneur",
    credits: 2000,
    seats: 1,
    companies: 1,
    price: 99,
  },
  {
    name: "Growth",
    credits: 6000,
    seats: 6,
    companies: 1,
    price: 259,
  },
  {
    name: "Business",
    credits: 9000,
    seats: 9,
    companies: 3,
    price: 299,
  },
];

// Custom tier presets (starting points for custom configurations)
const CUSTOM_PRESETS = {
  "custom-a": {
    name: "Custom A",
    credits: 9000,
    seats: 9,
    companies: 3,
    price: 299,
    description: "9k credits, 9 seats, 3 companies",
    featureSet: "full",
  },
  "custom-b": {
    name: "Custom B",
    credits: 9000,
    seats: 15,
    companies: 1,
    price: 299,
    description: "9k credits, 15 seats, 1 company",
    featureSet: "full",
  },
  "chat-a": {
    name: "Chat A",
    credits: 2000,
    seats: 1,
    companies: 1,
    price: 50,
    description: "2k credits, 1 seat, 1 company (Chat only)",
    featureSet: "chat",
  },
  "chat-b": {
    name: "Chat B",
    credits: 5000,
    seats: 5,
    companies: 1,
    price: 75,
    description: "5k credits, 5 seats, 1 company (Chat only)",
    featureSet: "chat",
  },
};

// System default pricing (70% platform fee)
const SYSTEM_DEFAULTS = {
  entrepreneur: {
    annual: 99,
    monthly: 119,
  },
  business: {
    // Incremental pricing (above Business tier)
    maxCreditLimit: 20000,
    creditIncrement: 1000,
    pricePerCredit1k: 20, // $20 per 1000 credits (above 9000)

    maxSeats: 60,
    pricePerSeat: 24, // $24 per seat (above 9)

    maxCompanies: 30,
    pricePerCompany: 40, // $40 per company (above 3)
  },
};

const PLATFORM_FEE_PERCENTAGE = 0.7; // 70% goes to platform

// Find which preset tier a configuration belongs to
function findPresetTier(credits: number, seats: number, companies: number) {
  return PRESET_TIERS.find(
    (tier) =>
      tier.credits === credits &&
      tier.seats === seats &&
      tier.companies === companies
  );
}

// Determine which tier to snap to based on the changed value
function getSnapToTier(
  field: "credits" | "seats" | "companies",
  value: number
): (typeof PRESET_TIERS)[0] | null {
  const businessTier = PRESET_TIERS[2]; // Business tier (9000, 9, 3)

  if (field === "credits") {
    if (value <= 2000) return PRESET_TIERS[0]; // Starter
    if (value <= 6000) return PRESET_TIERS[1]; // Growth
    if (value <= 9000) return PRESET_TIERS[2]; // Business
    return null; // Custom (above business tier)
  }

  if (field === "seats") {
    if (value <= 1) return PRESET_TIERS[0]; // Starter
    if (value <= 6) return PRESET_TIERS[1]; // Growth
    if (value <= 9) return PRESET_TIERS[2]; // Business
    return null; // Custom
  }

  if (field === "companies") {
    if (value <= 1) return PRESET_TIERS[1]; // Growth (max with 1 company)
    if (value <= 3) return PRESET_TIERS[2]; // Business
    return null; // Custom
  }

  return null;
}

interface SubscriptionTier {
  id: string;
  tier_name: string;
  tier_type: string;
  preset_type?: string;
  tier_description: string;
  display_order: number;
  is_active: boolean;
  is_featured: boolean;
  credit_limit: number;
  seats: number;
  companies: number;
  whitelabel_limit: number;
  pricing: any;
  features: any;
  features_header?: string;
  features_text?: string;
  is_system?: boolean;
  has_configuration?: boolean;
}

interface TierEditorDialogProps {
  open: boolean;
  onClose: () => void;
  tier: SubscriptionTier | null;
  whitelabelId: string;
  getToken: Function;
  onSave: () => void;
  eventListener?: (event: { type: string; error?: string; message?: string }) => void;
}

// Calculate minimum price based on configuration (70% of system defaults)
function calculateMinimumPrice(
  tierType: string,
  credits: number,
  seats: number,
  companies: number,
  hasConfiguration: boolean,
  selectedPreset?: string
): { annual: number; monthly: number; tierName?: string } {
  // Chat presets have fixed platform prices
  if (selectedPreset === "chat-a") {
    const platformPrice = CUSTOM_PRESETS["chat-a"].price; // $50
    return {
      annual: platformPrice,
      monthly: Math.round(platformPrice * 1.2),
      tierName: "Chat A",
    };
  }

  if (selectedPreset === "chat-b") {
    const platformPrice = CUSTOM_PRESETS["chat-b"].price; // $75
    return {
      annual: platformPrice,
      monthly: Math.round(platformPrice * 1.2),
      tierName: "Chat B",
    };
  }

  // entrepreneur tiers use simple flat pricing
  if (tierType === "entrepreneur") {
    return {
      annual: Math.round(
        SYSTEM_DEFAULTS.entrepreneur.annual * PLATFORM_FEE_PERCENTAGE
      ),
      monthly: Math.round(
        SYSTEM_DEFAULTS.entrepreneur.monthly * PLATFORM_FEE_PERCENTAGE
      ),
    };
  }

  // Non-entrepreneur tiers use tiered/variable pricing (business, custom, hybrid, etc.)
  if (tierType !== "entrepreneur") {
    console.log("Calculating minimum price:", {
      tierType,
      credits,
      seats,
      companies,
      hasConfiguration,
    });

    // Check if this matches a preset tier
    const presetTier = findPresetTier(credits, seats, companies);

    if (presetTier) {
      // Use preset tier pricing
      const annualPrice = presetTier.price;
      const monthlyPrice = Math.round(annualPrice * 1.2);

      console.log(`Using preset tier: ${presetTier.name}`, {
        annualPrice,
        monthlyPrice,
      });

      return {
        annual: Math.round(annualPrice * PLATFORM_FEE_PERCENTAGE),
        monthly: Math.round(monthlyPrice * PLATFORM_FEE_PERCENTAGE),
        tierName: presetTier.name,
      };
    }

    // Above Business tier - use incremental pricing
    const businessTier = PRESET_TIERS[2];
    let annualPrice = businessTier.price;

    // Add additional credits beyond 9000
    const additionalCredits = Math.max(0, credits - businessTier.credits);
    const creditPackages = Math.floor(
      additionalCredits / SYSTEM_DEFAULTS.business.creditIncrement
    );
    const creditCost =
      creditPackages * SYSTEM_DEFAULTS.business.pricePerCredit1k;

    // Add additional seats beyond 9
    const additionalSeats = Math.max(0, seats - businessTier.seats);
    const seatCost = additionalSeats * SYSTEM_DEFAULTS.business.pricePerSeat;

    // Add additional companies beyond 3
    const additionalCompanies = Math.max(0, companies - businessTier.companies);
    const companyCost =
      additionalCompanies * SYSTEM_DEFAULTS.business.pricePerCompany;

    // Total annual price
    annualPrice += creditCost + seatCost + companyCost;

    // Monthly is annual + 20%
    const monthlyPrice = Math.round(annualPrice * 1.2);

    console.log("Custom tier pricing breakdown:", {
      basePrice: businessTier.price,
      creditPackages,
      creditCost,
      additionalSeats,
      seatCost,
      additionalCompanies,
      companyCost,
      totalAnnual: annualPrice,
      totalMonthly: monthlyPrice,
    });

    const result = {
      annual: Math.round(annualPrice * PLATFORM_FEE_PERCENTAGE),
      monthly: Math.round(monthlyPrice * PLATFORM_FEE_PERCENTAGE),
      tierName: "Custom",
    };
    console.log("Final minimum (70% of system):", result);
    return result;
  }

  // Entrepreneur tier - use flat pricing
  return {
    annual: Math.round(
      SYSTEM_DEFAULTS.entrepreneur.annual * PLATFORM_FEE_PERCENTAGE
    ),
    monthly: Math.round(
      SYSTEM_DEFAULTS.entrepreneur.monthly * PLATFORM_FEE_PERCENTAGE
    ),
  };
}

// Calculate margin (difference between price and platform fee)
function calculateMargin(price: number, minimumPrice: number): number {
  return Math.max(0, price - minimumPrice);
}

// Calculate margin percentage
function calculateMarginPercentage(
  price: number,
  minimumPrice: number
): number {
  if (price === 0) return 0;
  return Math.round(((price - minimumPrice) / price) * 100);
}

export default function TierEditorDialog({
  open,
  onClose,
  tier,
  whitelabelId,
  getToken,
  onSave,
  eventListener,
}: TierEditorDialogProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<any>({
    tier_name: "",
    tier_description: "",
    credit_limit: 1000,
    seats: 1,
    companies: 1,
    whitelabel_limit: 0,
    is_active: true,
    is_featured: false,
    display_order: 0,
    features_header: "",
    features_text: "",
    has_configuration: false,
    pricing: {
      monthly: { price: 0 },
      annual: { price: 0 },
    },
  });

  const [minimumPricing, setMinimumPricing] = useState({
    annual: 0,
    monthly: 0,
  });
  const [margin, setMargin] = useState({ annual: 0, monthly: 0 });
  const [marginPercentage, setMarginPercentage] = useState({
    annual: 0,
    monthly: 0,
  });
  const [selectedPreset, setSelectedPreset] = useState<string>("entrepreneur"); // entrepreneur, growth, business, custom-a, custom-b
  const [featuresDialogOpen, setFeaturesDialogOpen] = useState(false);
  const [tierFeatures, setTierFeatures] = useState<{
    [key: string]: FeatureState;
  }>({});

  useEffect(() => {
    if (tier) {
      const data = {
        tier_name: tier.tier_name,
        tier_description: tier.tier_description,
        credit_limit: tier.credit_limit,
        seats: tier.seats,
        companies: tier.companies,
        whitelabel_limit: tier.whitelabel_limit,
        is_active: tier.is_active,
        is_featured: tier.is_featured,
        display_order: tier.display_order,
        features_header: tier.features_header || "",
        features_text: tier.features_text || "",
        has_configuration: tier.has_configuration || false,
        pricing: {
          monthly: tier.pricing?.monthly || { price: 0 },
          annual: tier.pricing?.annual || { price: 0 },
        },
      };
      setFormData(data);

      // Load existing features or use defaults based on tier name
      if (tier.features && Object.keys(tier.features).length > 0) {
        setTierFeatures(tier.features);
      } else {
        // Use default features based on tier name
        setTierFeatures(getDefaultFeaturesForTier(tier.tier_name));
      }

      // Use the stored preset_type as the selected preset
      setSelectedPreset(tier.preset_type || "custom-a");
    } else {
      // Reset form for new tier creation - default to Entrepreneur
      const entrepreneurTier = PRESET_TIERS[0];
      setFormData({
        tier_name: "",
        tier_description: "",
        credit_limit: entrepreneurTier.credits,
        seats: entrepreneurTier.seats,
        companies: entrepreneurTier.companies,
        whitelabel_limit: 0,
        is_active: true,
        is_featured: false,
        display_order: 0,
        features_header: "",
        features_text: "",
        has_configuration: false,
        pricing: {
          monthly: { price: 0 },
          annual: { price: 0 },
        },
      });
      setSelectedPreset("entrepreneur");

      // Set default features for Entrepreneur tier
      setTierFeatures(getDefaultFeaturesForTier("entrepreneur"));
    }
  }, [tier, open]);

  // Recalculate minimum pricing and margin whenever configuration changes
  useEffect(() => {
    const tierType = tier?.tier_type || "custom";
    const hasConfiguration = formData.has_configuration || false;

    console.log("useEffect recalculating:", {
      tierType,
      hasConfiguration,
      credits: formData.credit_limit,
      seats: formData.seats,
      companies: formData.companies,
    });

    const minPricing = calculateMinimumPrice(
      tierType,
      formData.credit_limit,
      formData.seats,
      formData.companies,
      hasConfiguration,
      selectedPreset
    );

    setMinimumPricing(minPricing);

    // Auto-set pricing to 30% margin if price is 0 or below minimum
    const currentAnnualPrice = formData.pricing?.annual?.price || 0;
    let finalAnnualPrice = currentAnnualPrice;
    let finalMonthlyPrice = formData.pricing?.monthly?.price || 0;

    if (currentAnnualPrice === 0 || currentAnnualPrice < minPricing.annual) {
      // Calculate price for 30% margin: Price = Minimum / (1 - 0.30)
      const suggestedAnnual = Math.round(
        minPricing.annual / PLATFORM_FEE_PERCENTAGE
      );
      const suggestedMonthly = Math.round(suggestedAnnual * 1.2);

      finalAnnualPrice = suggestedAnnual;
      finalMonthlyPrice = suggestedMonthly;

      setFormData((prev: any) => ({
        ...prev,
        pricing: {
          annual: { price: suggestedAnnual, platform_price: minPricing.annual },
          monthly: {
            price: suggestedMonthly,
            platform_price: minPricing.monthly,
          },
        },
      }));
    }

    // Calculate margins using the final pricing values (including any auto-updates)
    setMargin({
      annual: calculateMargin(finalAnnualPrice, minPricing.annual),
      monthly: calculateMargin(finalMonthlyPrice, minPricing.monthly),
    });

    setMarginPercentage({
      annual: calculateMarginPercentage(finalAnnualPrice, minPricing.annual),
      monthly: calculateMarginPercentage(finalMonthlyPrice, minPricing.monthly),
    });
  }, [
    formData.credit_limit,
    formData.seats,
    formData.companies,
    formData.has_configuration,
    formData.pricing,
    tier?.tier_type,
    selectedPreset,
  ]);

  const handleSave = async () => {
    // Validate pricing meets minimum requirements
    const annualPrice = formData.pricing?.annual?.price || 0;
    const monthlyPrice = formData.pricing?.monthly?.price || 0;

    if (annualPrice < minimumPricing.annual) {
      eventListener?.({
        type: "SET_ERROR",
        error: `Annual price must be at least $${minimumPricing.annual} to cover the 70% platform fee.`,
      });
      return;
    }

    if (monthlyPrice < minimumPricing.monthly) {
      eventListener?.({
        type: "SET_ERROR",
        error: `Monthly price must be at least $${minimumPricing.monthly} to cover the 70% platform fee.`,
      });
      return;
    }

    setLoading(true);

    try {
      const token = await getToken();

      // If no tier, create a new tier
      if (!tier) {
        // Add preset_type and features to the form data for creation
        const createData = {
          ...formData,
          preset_type: selectedPreset,
          features: tierFeatures, // Include the configured features
        };

        await axios.post(
          `${hostname}/whitelabel/${whitelabelId}/subscription-tiers`,
          createData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else {
        // Update existing tier (all tiers now have UUIDs in database)
        const updateData = {
          ...formData,
          features: tierFeatures, // Include the configured features
        };

        await axios.put(
          `${hostname}/whitelabel/${whitelabelId}/subscription-tiers/${tier.id}`,
          updateData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      onSave();
      onClose();
    } catch (err: any) {
      console.error("Failed to save tier:", err);
      eventListener?.({
        type: "SET_ERROR",
        error: "Failed to save subscription tier. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePresetChange = (preset: string) => {
    // Handle legacy "custom" value - map to custom-a for backwards compatibility
    const normalizedPreset = preset === "custom" ? "custom-a" : preset;
    setSelectedPreset(normalizedPreset);

    // Check if this is a custom preset (custom-a, custom-b, chat-a, chat-b)
    const customPreset =
      CUSTOM_PRESETS[normalizedPreset as keyof typeof CUSTOM_PRESETS];
    if (customPreset) {
      // Apply custom preset configuration
      setFormData((prev: any) => ({
        ...prev,
        credit_limit: customPreset.credits,
        seats: customPreset.seats,
        companies: customPreset.companies,
      }));
      // Set features based on feature set type
      if (customPreset.featureSet === "chat") {
        // Include collaborators for chat presets with multiple seats (e.g., chat-b has 5 seats)
        const includeCollaborators = customPreset.seats > 1;
        setTierFeatures(getChatOnlyFeatures(includeCollaborators));
      } else {
        setTierFeatures(getDefaultFeaturesForTier("business"));
      }
    } else {
      // Apply standard preset tier configuration
      const presetTier = PRESET_TIERS.find(
        (t) => t.name.toLowerCase() === normalizedPreset
      );
      if (presetTier) {
        setFormData((prev: any) => ({
          ...prev,
          credit_limit: presetTier.credits,
          seats: presetTier.seats,
          companies: presetTier.companies,
        }));
        // Update features based on the preset
        setTierFeatures(getDefaultFeaturesForTier(presetTier.name));
      }
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev: any) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // If changing credits, seats, or companies in custom mode, recalculate minimum
      if (
        field === "credit_limit" ||
        field === "seats" ||
        field === "companies"
      ) {
        const tierType = tier?.tier_type || "custom";
        const hasConfiguration = prev.has_configuration || false;

        const newMinPricing = calculateMinimumPrice(
          tierType,
          field === "credit_limit" ? value : prev.credit_limit,
          field === "seats" ? value : prev.seats,
          field === "companies" ? value : prev.companies,
          hasConfiguration,
          selectedPreset
        );

        // Ensure current pricing meets new minimum
        const currentAnnual = prev.pricing?.annual?.price || 0;
        const currentMonthly = prev.pricing?.monthly?.price || 0;

        if (
          currentAnnual < newMinPricing.annual ||
          currentMonthly < newMinPricing.monthly
        ) {
          newData.pricing = {
            annual: { price: Math.max(currentAnnual, newMinPricing.annual) },
            monthly: { price: Math.max(currentMonthly, newMinPricing.monthly) },
          };
        }
      }

      return newData;
    });
  };

  const handlePricingChange = (
    period: string,
    value: number,
    platform_fee: number
  ) => {
    setFormData((prev: any) => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [period]: { price: value, platform_price: platform_fee },
      },
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">
          {!tier ? "Create New Subscription Tier" : "Edit Subscription Tier"}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* Show info alert when editing */}
          {tier && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Note:</strong> The tier name cannot be changed after
                creation as it's used to identify subscriptions. You can modify
                pricing, features, and other settings.
              </Typography>
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tier Name"
                value={formData.tier_name}
                onChange={(e) => handleChange("tier_name", e.target.value)}
                disabled={!!tier} // Disable when editing existing tier
                helperText={
                  tier
                    ? "Tier name cannot be changed after creation"
                    : formData.tier_name
                    ? `Tier ID will be: ${formData.tier_name
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/^-|-$/g, "")}`
                    : "Choose a unique name for this tier"
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={formData.display_order}
                onChange={(e) =>
                  handleChange("display_order", parseInt(e.target.value))
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.tier_description}
                onChange={(e) =>
                  handleChange("tier_description", e.target.value)
                }
              />
            </Grid>

            {/* Preset Tier Selector */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Configuration Preset{" "}
                {tier && "(Cannot be changed after creation)"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset" disabled={!!tier}>
                <RadioGroup
                  row
                  value={selectedPreset}
                  onChange={(e) => handlePresetChange(e.target.value)}
                >
                  <FormControlLabel
                    value="entrepreneur"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          Entrepreneur
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          2k credits, 1 seat, 1 company
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="growth"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          Growth
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          6k credits, 6 seats, 1 company
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="business"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          Business
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          9k credits, 9 seats, 3 companies
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="custom-a"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          Custom A
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {CUSTOM_PRESETS["custom-a"].description}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="custom-b"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          Custom B
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {CUSTOM_PRESETS["custom-b"].description}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="chat-a"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          Chat A
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {CUSTOM_PRESETS["chat-a"].description}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="chat-b"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          Chat B
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {CUSTOM_PRESETS["chat-b"].description}
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Limits */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Limits & Quotas
                {selectedPreset !== "custom-a" &&
                  selectedPreset !== "custom-b" &&
                  " (Locked to preset)"}
              </Typography>
            </Grid>

            <Grid item xs={6} md={4}>
              <TextField
                fullWidth
                label="Credits/Month"
                type="number"
                value={formData.credit_limit}
                onChange={(e) => {
                  const customPreset =
                    CUSTOM_PRESETS[
                      selectedPreset as keyof typeof CUSTOM_PRESETS
                    ];
                  const minCredits =
                    customPreset?.credits || PRESET_TIERS[2].credits;
                  const value = parseInt(e.target.value) || minCredits;
                  handleChange("credit_limit", value);
                }}
                disabled={
                  selectedPreset !== "custom-a" && selectedPreset !== "custom-b"
                }
                inputProps={{
                  step: SYSTEM_DEFAULTS.business.creditIncrement,
                  min:
                    CUSTOM_PRESETS[
                      selectedPreset as keyof typeof CUSTOM_PRESETS
                    ]?.credits || PRESET_TIERS[2].credits,
                  max: SYSTEM_DEFAULTS.business.maxCreditLimit,
                }}
                helperText={
                  selectedPreset === "custom-a" || selectedPreset === "custom-b"
                    ? `Min: ${
                        CUSTOM_PRESETS[
                          selectedPreset as keyof typeof CUSTOM_PRESETS
                        ]?.credits / 1000
                      }k, Max: ${
                        SYSTEM_DEFAULTS.business.maxCreditLimit / 1000
                      }k. +$${
                        SYSTEM_DEFAULTS.business.pricePerCredit1k
                      } per 1k above base`
                    : "Locked by preset"
                }
              />
            </Grid>

            <Grid item xs={6} md={4}>
              <TextField
                fullWidth
                label="Seats"
                type="number"
                value={formData.seats}
                onChange={(e) => {
                  const customPreset =
                    CUSTOM_PRESETS[
                      selectedPreset as keyof typeof CUSTOM_PRESETS
                    ];
                  const minSeats = customPreset?.seats || PRESET_TIERS[2].seats;
                  const value = parseInt(e.target.value) || minSeats;
                  handleChange("seats", value);
                }}
                disabled={
                  selectedPreset !== "custom-a" && selectedPreset !== "custom-b"
                }
                inputProps={{
                  min:
                    CUSTOM_PRESETS[
                      selectedPreset as keyof typeof CUSTOM_PRESETS
                    ]?.seats || PRESET_TIERS[2].seats,
                  max: SYSTEM_DEFAULTS.business.maxSeats,
                }}
                helperText={
                  selectedPreset === "custom-a" || selectedPreset === "custom-b"
                    ? `Min: ${
                        CUSTOM_PRESETS[
                          selectedPreset as keyof typeof CUSTOM_PRESETS
                        ]?.seats
                      }, Max: ${SYSTEM_DEFAULTS.business.maxSeats}. +$${
                        SYSTEM_DEFAULTS.business.pricePerSeat
                      } per seat above base`
                    : "Locked by preset"
                }
              />
            </Grid>

            <Grid item xs={6} md={4}>
              <TextField
                fullWidth
                label="Companies"
                type="number"
                value={formData.companies}
                onChange={(e) => {
                  const customPreset =
                    CUSTOM_PRESETS[
                      selectedPreset as keyof typeof CUSTOM_PRESETS
                    ];
                  const minCompanies =
                    customPreset?.companies || PRESET_TIERS[2].companies;
                  const value = parseInt(e.target.value) || minCompanies;
                  handleChange("companies", value);
                }}
                disabled={
                  selectedPreset !== "custom-a" && selectedPreset !== "custom-b"
                }
                inputProps={{
                  min:
                    CUSTOM_PRESETS[
                      selectedPreset as keyof typeof CUSTOM_PRESETS
                    ]?.companies || PRESET_TIERS[2].companies,
                  max: SYSTEM_DEFAULTS.business.maxCompanies,
                }}
                helperText={
                  selectedPreset === "custom-a" || selectedPreset === "custom-b"
                    ? `Min: ${
                        CUSTOM_PRESETS[
                          selectedPreset as keyof typeof CUSTOM_PRESETS
                        ]?.companies
                      }, Max: ${SYSTEM_DEFAULTS.business.maxCompanies}. +$${
                        SYSTEM_DEFAULTS.business.pricePerCompany
                      } per company above base`
                    : "Locked by preset"
                }
              />
            </Grid>

            {/* Pricing */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Pricing
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Set the annual monthly price. Monthly subscriptions will
                automatically be 20% higher.
              </Typography>
            </Grid>

            {/* Platform Fee & Margin Information */}
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                  Platform Fee & Your Margin
                </Typography>

                {/* Show calculation breakdown for business/configurable tiers */}
                {(tier?.tier_type === "business" ||
                  formData.has_configuration) &&
                  (() => {
                    const presetTier = findPresetTier(
                      formData.credit_limit,
                      formData.seats,
                      formData.companies
                    );
                    const businessTier = PRESET_TIERS[2];

                    return (
                      <Box
                        sx={{
                          mb: 1,
                          pl: 1,
                          borderLeft: 2,
                          borderColor: "primary.main",
                        }}
                      >
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ fontWeight: "bold", mb: 0.5 }}
                        >
                          {presetTier
                            ? `${presetTier.name} Tier`
                            : "Custom Configuration"}
                        </Typography>

                        {presetTier ? (
                          <>
                            <Typography variant="caption" display="block">
                              {presetTier.credits.toLocaleString()} credits,{" "}
                              {presetTier.seats} seats, {presetTier.companies}{" "}
                              companies
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ fontWeight: "bold", mt: 0.5 }}
                            >
                              System Total: ${presetTier.price}/mo (annual)
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography variant="caption" display="block">
                              Base (Business tier): ${businessTier.price}/mo
                            </Typography>
                            {formData.credit_limit > businessTier.credits && (
                              <Typography variant="caption" display="block">
                                +{" "}
                                {Math.floor(
                                  (formData.credit_limit -
                                    businessTier.credits) /
                                    SYSTEM_DEFAULTS.business.creditIncrement
                                )}{" "}
                                extra credit packages × $
                                {SYSTEM_DEFAULTS.business.pricePerCredit1k}/mo =
                                $
                                {Math.floor(
                                  (formData.credit_limit -
                                    businessTier.credits) /
                                    SYSTEM_DEFAULTS.business.creditIncrement
                                ) * SYSTEM_DEFAULTS.business.pricePerCredit1k}
                                /mo
                              </Typography>
                            )}
                            {formData.seats > businessTier.seats && (
                              <Typography variant="caption" display="block">
                                + {formData.seats - businessTier.seats} extra
                                seats × ${SYSTEM_DEFAULTS.business.pricePerSeat}
                                /mo = $
                                {(formData.seats - businessTier.seats) *
                                  SYSTEM_DEFAULTS.business.pricePerSeat}
                                /mo
                              </Typography>
                            )}
                            {formData.companies > businessTier.companies && (
                              <Typography variant="caption" display="block">
                                + {formData.companies - businessTier.companies}{" "}
                                extra companies × $
                                {SYSTEM_DEFAULTS.business.pricePerCompany}/mo =
                                $
                                {(formData.companies - businessTier.companies) *
                                  SYSTEM_DEFAULTS.business.pricePerCompany}
                                /mo
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ fontWeight: "bold", mt: 0.5 }}
                            >
                              System Total: $
                              {Math.round(
                                minimumPricing.annual / PLATFORM_FEE_PERCENTAGE
                              )}
                              /mo (annual)
                            </Typography>
                          </>
                        )}
                      </Box>
                    );
                  })()}

                <Typography variant="body2" sx={{ mb: 1, fontWeight: "bold" }}>
                  Minimum Price (70% Platform Fee): ${minimumPricing.annual}/mo
                  (annual) • ${minimumPricing.monthly}/mo (monthly)
                </Typography>
                {formData.pricing?.annual?.price > 0 && (
                  <>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Your Margin: ${margin.annual}/mo (annual) • $
                      {margin.monthly}/mo (monthly)
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      Margin Percentage: {marginPercentage.annual}% (annual) •{" "}
                      {marginPercentage.monthly}% (monthly)
                    </Typography>
                  </>
                )}
              </Alert>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Annual Monthly Price ($)"
                type="number"
                value={formData.pricing?.annual?.price || 0}
                onChange={(e) => {
                  const annualPrice = Math.max(
                    minimumPricing.annual,
                    parseFloat(e.target.value) || 0
                  );
                  const monthlyPrice = Math.round(annualPrice * 1.2);
                  handlePricingChange(
                    "annual",
                    annualPrice,
                    minimumPricing.annual
                  );
                  handlePricingChange(
                    "monthly",
                    monthlyPrice,
                    minimumPricing.monthly
                  );
                }}
                helperText={`Price per month when billed annually (min: $${minimumPricing.annual})`}
                error={formData.pricing?.annual?.price < minimumPricing.annual}
                inputProps={{ min: minimumPricing.annual, step: 1 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Monthly Price ($)"
                type="number"
                value={formData.pricing?.monthly?.price || 0}
                disabled
                helperText={`Automatically calculated (+20% from annual, min: $${minimumPricing.monthly})`}
              />
            </Grid>

            {/* Features */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 2,
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Features Configuration
                  {(selectedPreset === "chat-a" ||
                    selectedPreset === "chat-b") &&
                    " (Locked for Chat presets)"}
                </Typography>
                {selectedPreset !== "chat-a" && selectedPreset !== "chat-b" && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Settings />}
                    onClick={() => setFeaturesDialogOpen(true)}
                  >
                    Configure Feature Access
                  </Button>
                )}
              </Box>
            </Grid>

            {/* Features Summary */}
            <Grid item xs={12}>
              <Alert
                severity={
                  selectedPreset === "chat-a" || selectedPreset === "chat-b"
                    ? "info"
                    : "success"
                }
                sx={{ mb: 2 }}
              >
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                  {selectedPreset === "chat-a" || selectedPreset === "chat-b"
                    ? "Chat-Only Features (Fixed)"
                    : "Feature Access Summary"}
                </Typography>
                {(selectedPreset === "chat-a" ||
                  selectedPreset === "chat-b") && (
                  <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                    Chat presets include only: Dashboard, Chat, and Employees.
                    All other features are hidden and cannot be modified.
                  </Typography>
                )}
                <Typography variant="caption" component="div">
                  • Enabled Features:{" "}
                  {
                    Object.values(tierFeatures).filter(
                      (state) => state === FEATURE_STATES.ENABLED
                    ).length
                  }
                </Typography>
                <Typography variant="caption" component="div">
                  • Locked Features:{" "}
                  {
                    Object.values(tierFeatures).filter(
                      (state) => state === FEATURE_STATES.LOCKED
                    ).length
                  }
                </Typography>
                <Typography variant="caption" component="div">
                  • Hidden Features:{" "}
                  {
                    Object.values(tierFeatures).filter(
                      (state) => state === FEATURE_STATES.HIDDEN
                    ).length
                  }
                </Typography>
              </Alert>
            </Grid>

            {/* Features Display Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Features Display Text
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Features Header"
                placeholder="e.g., Includes all previous tier features plus:"
                value={formData.features_header}
                onChange={(e) =>
                  handleChange("features_header", e.target.value)
                }
                helperText="Text that appears above the features list on the pricing page"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Features Text"
                placeholder="Enter each feature on a new line"
                multiline
                rows={6}
                value={formData.features_text}
                onChange={(e) => handleChange("features_text", e.target.value)}
                helperText="Enter features one per line. These will appear on the pricing page."
              />
            </Grid>

            {/* Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Settings
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) =>
                      handleChange("is_active", e.target.checked)
                    }
                  />
                }
                label="Active"
              />
            </Grid>

            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_featured}
                    onChange={(e) =>
                      handleChange("is_featured", e.target.checked)
                    }
                  />
                }
                label="Featured"
              />
            </Grid>

            {/* Business Options Configuration */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Business Options
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.has_configuration}
                    onChange={(e) =>
                      handleChange("has_configuration", e.target.checked)
                    }
                  />
                }
                label="Enable Configuration Options"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Allow users to customize seats, companies, and credit packages
                for this tier. Pricing for these is managed by the platform.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.whitelabel_limit > 0}
                    onChange={(e) =>
                      handleChange("whitelabel_limit", e.target.checked ? 1 : 0)
                    }
                  />
                }
                label="Enable Whitelabel"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Allow subscribers on this tier to create their own whitelabel
                instances. When disabled, whitelabel features will be hidden
                from subscribers.
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          onClick={handleSave}
          variant="contained"
          loading={loading}
        >
          {!tier ? "Create Tier" : "Save Changes"}
        </LoadingButton>
      </DialogActions>

      {/* Features Configuration Dialog */}
      <TierFeaturesDialog
        open={featuresDialogOpen}
        onClose={() => setFeaturesDialogOpen(false)}
        features={tierFeatures}
        onSave={(newFeatures) => {
          setTierFeatures(newFeatures);
          setFeaturesDialogOpen(false);
        }}
        tierName={formData.tier_name}
        whitelabelId={whitelabelId}
        getToken={getToken as () => Promise<string>}
      />
    </Dialog>
  );
}
